import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp, onSnapshot, deleteField } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { logActivity } from "../activityLog";
import { db, storage } from "../utils/firebase";
import ImageCropper from "./ImageCropper";

type Mode = "edit" | "preview";
type GenStatus = "queued" | "running" | "ready" | "failed";
interface Category { id: string; name: Record<string, string>; }
const LANG = "en";

const calcReadTime = (text: string): string => {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
};

const storagePathFromURL = (url: string): string | null => {
  try {
    const match = url.match(/\/o\/(.+?)(\?|$)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch { return null; }
};

const REWRITE_FN_URL = "https://us-central1-ama-top.cloudfunctions.net/rewriteArticle";
const GEN_FN_URL = "https://us-central1-ama-top.cloudfunctions.net/generateArticle";

const FM_RE = /^---\n([\s\S]*?)\n---\n?/;

const toRelativePath = (url: string, project: string): string => {
  if (!url) return "";
  const match = url.match(/\/o\/(.+?)(\?|$)/);
  if (!match) return url;
  const fullPath = decodeURIComponent(match[1]);
  return "/" + fullPath.replace(`content/${project}/`, "content/");
};

const parseFrontmatter = (raw: string): { meta: Record<string, string>; body: string } => {
  const match = raw.match(FM_RE);
  if (!match) return { meta: {}, body: raw };
  const meta: Record<string, string> = {};
  for (const line of match[1].split("\n")) {
    const idx = line.indexOf(":");
    if (idx < 0) continue;
    const key = line.slice(0, idx).trim();
    const val = line.slice(idx + 1).trim().replace(/^["']|["']$/g, "");
    meta[key] = val;
  }
  return { meta, body: raw.slice(match[0].length) };
};


const Editor: React.FC = () => {
  const { project, contentType, docId } = useParams<{ project: string; contentType: string; docId: string }>();
  const navigate = useNavigate();
  const { user, firebaseUser } = useAuth();

  const [markdown, setMarkdown] = useState("");
  const [category, setCategory] = useState("");
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(true);
  const [mdLoading, setMdLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState<Mode>("edit");
  const [categories, setCategories] = useState<Category[]>([]);
  const [catOpen, setCatOpen] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [croppedPreview, setCroppedPreview] = useState("");
  const [showRewrite, setShowRewrite] = useState(false);
  const [genStatus, setGenStatus] = useState<GenStatus | undefined>(undefined);
  const [genError, setGenError] = useState<string | undefined>(undefined);
  const [genMeta, setGenMeta] = useState<any>(null);
  const [retrying, setRetrying] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const justSavedRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const STUCK_AFTER_MS = 30 * 60 * 1000;
  const genStartedMs =
    genMeta?.startedAt?.toMillis?.() ||
    genMeta?.requestedAt?.toMillis?.() ||
    null;
  const isStuck =
    (genStatus === "queued" || genStatus === "running") &&
    genStartedMs !== null &&
    now - genStartedMs > STUCK_AFTER_MS;

  const readTime = useMemo(() => {
    const { body } = parseFrontmatter(markdown);
    return calcReadTime(body);
  }, [markdown]);

  useEffect(() => {
    if (!project) return;
    getDoc(doc(db, "content", project, "_config", "insights"))
      .then((snap) => {
        if (snap.exists()) {
          const raw = snap.data().categories || [];
          setCategories(raw.map((c: any) =>
            typeof c === "string" ? { id: c.toLowerCase().replace(/[^a-z0-9]+/g, "-"), name: { [LANG]: c } } : c
          ));
        }
      })
      .catch(() => {});
  }, [project]);

  const mdStoragePath = `content/${project}/${contentType}/${docId}/${LANG}/markdown.md`;

  useEffect(() => {
    if (!project || !contentType || !docId) return;
    let lastMdLoadKey = "";
    setLoading(true);

    const unsub = onSnapshot(
      doc(db, "content", project, contentType, docId),
      (snap) => {
        const d = snap.exists() ? snap.data() : ({} as any);
        setCategory(d.category || "");
        setPreview(d.preview || "");
        setCroppedPreview(d.croppedPreview || "");
        setGenStatus(d.generation?.status);
        setGenError(d.generation?.error);
        setGenMeta(d.generation || null);
        setLoading(false);

        const mdKey = `${d.updatedBy || ""}|${d.updatedAt?.toMillis?.() || ""}`;
        const hasMarkdown = !!d.updatedBy;
        const genActive = d.generation?.status === "queued" || d.generation?.status === "running";

        if (!hasMarkdown) {
          setMarkdown("");
          return;
        }
        if (genActive) return;
        if (mdKey === lastMdLoadKey) return;
        lastMdLoadKey = mdKey;
        if (justSavedRef.current) {
          justSavedRef.current = false;
          return;
        }

        setMdLoading(true);
        (async () => {
          try {
            const url = await getDownloadURL(ref(storage, mdStoragePath));
            const text = await fetch(url).then((r) => r.text());
            setMarkdown(text);
          } catch {
            setMarkdown("");
          } finally {
            setMdLoading(false);
          }
        })();
      },
      (err) => {
        console.error("[Editor] Snapshot error:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [project, contentType, docId, mdStoragePath]);

  const save = async () => {
    if (!project || !contentType || !docId) return;
    const { meta } = parseFrontmatter(markdown);
    const docTitle = meta.title || docId;
    const docDescription = meta.description || "";
    setSaving(true);
    justSavedRef.current = true;
    try {
      const mdBlob = new Blob([markdown], { type: "text/markdown" });
      await Promise.all([
        uploadBytes(ref(storage, mdStoragePath), mdBlob),
        updateDoc(doc(db, "content", project, contentType, docId), {
          title: { [LANG]: docTitle },
          description: { [LANG]: docDescription },
          category,
          preview,
          readTime,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        }),
      ]);
      toast.success("Saved");
      logActivity(project, {
        action: "edit",
        contentType,
        docId,
        docTitle,
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
    } catch (err) {
      console.error("[Editor] Save error:", err);
      toast.error("Save failed");
    }
    setSaving(false);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  const retryGeneration = async () => {
    if (!project || !contentType || !docId || !genMeta) return;
    const kind = genMeta.kind || "generate";
    const url = kind === "rewrite" ? REWRITE_FN_URL : GEN_FN_URL;
    const body =
      kind === "rewrite"
        ? {
            project,
            contentType,
            docId,
            locale: genMeta.locale || LANG,
            direction: genMeta.direction || "",
            tone: genMeta.tone || "",
            length: genMeta.length || "medium",
            notes: genMeta.notes || "",
            user: user?.email || firebaseUser?.email || "unknown",
          }
        : {
            project,
            contentType,
            docId,
            topic: genMeta.topic || "",
            category: genMeta.category || "",
            tone: genMeta.tone || "",
            length: genMeta.length || "medium",
            locale: genMeta.locale || LANG,
            notes: genMeta.notes || "",
            user: user?.email || firebaseUser?.email || "unknown",
          };

    if (kind === "generate" && !body.topic) {
      toast.error("Cannot retry: original topic missing");
      return;
    }

    setRetrying(true);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      toast.success("Retry queued");
    } catch (e: any) {
      toast.error(`Retry failed: ${e.message}`);
    }
    setRetrying(false);
  };

  const clearStuckGeneration = async () => {
    if (!project || !contentType || !docId) return;
    if (!window.confirm("Clear the stuck generation marker? This won't affect the actual pipeline run.")) return;
    try {
      await updateDoc(doc(db, "content", project, contentType, docId), {
        generation: deleteField(),
      });
      toast.success("Cleared");
    } catch (e: any) {
      toast.error(`Clear failed: ${e.message}`);
    }
  };

  const compressImage = (file: File): Promise<{ blob: Blob; ext: string }> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1600;
        let { width: w, height: h } = img;
        if (w > MAX || h > MAX) {
          const scale = MAX / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (webp) => {
            if (!webp) { reject(new Error("toBlob failed")); return; }
            if (webp.size < file.size) {
              resolve({ blob: webp, ext: "webp" });
            } else {
              resolve({ blob: file, ext: file.name.split(".").pop() || "png" });
            }
          },
          "image/webp",
          0.85
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  const uploadPreview = async (file: File) => {
    if (!project || !docId) return;
    setUploading(true);
    try {
      const { blob, ext } = await compressImage(file);
      const path = `content/${project}/${contentType}/${docId}/preview.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      setPreview(url);
      toast.success("Image uploaded");
    } catch (err) {
      console.error("[Editor] Upload error:", err);
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  const handleDelete = async () => {
    if (!project || !contentType || !docId) return;
    const { meta } = parseFrontmatter(markdown);
    if (!window.confirm(`Delete "${meta.title || docId}"?`)) return;
    try {
      await deleteDoc(doc(db, "content", project, contentType, docId));
      toast.success("Deleted");
      navigate(`/cms/${project}/${contentType}`);
    } catch (err) {
      console.error("[Editor] Delete error:", err);
      toast.error("Delete failed");
    }
  };

  const renderMarkdownPreview = (md: string) => {
    const escaped = md
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    const html = escaped
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
      .replace(/^[*-] (.+)$/gm, '<li>$1</li>')
      .replace(/^---$/gm, '<hr />')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br />');

    return `<p>${html}</p>`
      .replace(/<p><h([1-3])>/g, '<h$1>')
      .replace(/<\/h([1-3])><\/p>/g, '</h$1>')
      .replace(/<p><pre>/g, '<pre>')
      .replace(/<\/pre><\/p>/g, '</pre>')
      .replace(/<p><hr \/><\/p>/g, '<hr />')
      .replace(/<p><li>/g, '<ul><li>')
      .replace(/<\/li><\/p>/g, '</li></ul>')
      .replace(/<\/li><br \/><li>/g, '</li><li>');
  };

  if (loading) return <div className="editor__loading">Loading...</div>;

  const genActive = (genStatus === "queued" || genStatus === "running") && !isStuck;

  return (
    <div className={`editor ${genActive ? "editor--locked" : ""}`}>
      <div className="editor__header">
        <button
          className="editor__back"
          onClick={() => navigate(`/cms/${project}/${contentType}`)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        <div className="editor__actions">
          <div className="editor__mode-toggle">
            <button
              className={`editor__mode-btn ${mode === "edit" ? "editor__mode-btn--active" : ""}`}
              onClick={() => setMode("edit")}
            >
              Edit
            </button>
            <button
              className={`editor__mode-btn ${mode === "preview" ? "editor__mode-btn--active" : ""}`}
              onClick={() => setMode("preview")}
            >
              Preview
            </button>
          </div>
          <button className="editor__delete-btn" onClick={handleDelete} disabled={genActive}>Delete</button>
          <button
            className="editor__save-btn editor__save-btn--secondary"
            onClick={() => setShowRewrite(true)}
            disabled={!markdown.trim() || genActive}
            title={genActive ? "Generation in progress" : (!markdown.trim() ? "Nothing to rewrite yet" : "Rewrite with AI")}
          >
            ✨ Rewrite
          </button>
          <button className="editor__save-btn" onClick={save} disabled={saving || genActive}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="editor__meta">
        <div className="editor__meta-row">
          <div className="editor__meta-field">
            <span>Category</span>
            <div className={`editor__dropdown ${catOpen ? "editor__dropdown--open" : ""}`}>
              <button
                type="button"
                className="editor__dropdown-trigger"
                onClick={() => setCatOpen((v) => !v)}
                onBlur={() => setTimeout(() => setCatOpen(false), 150)}
              >
                <span className={category ? "" : "editor__dropdown-placeholder"}>
                  {category ? (categories.find((c) => c.id === category)?.name[LANG] || category) : "Select category"}
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {catOpen && (
                <div className="editor__dropdown-menu">
                  <button
                    type="button"
                    className={`editor__dropdown-item ${!category ? "editor__dropdown-item--active" : ""}`}
                    onMouseDown={() => { setCategory(""); setCatOpen(false); }}
                  >
                    None
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={`editor__dropdown-item ${category === c.id ? "editor__dropdown-item--active" : ""}`}
                      onMouseDown={() => { setCategory(c.id); setCatOpen(false); }}
                    >
                      {c.name[LANG] || c.id}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <label className="editor__meta-field editor__meta-field--readonly">
            <span>Read Time</span>
            <input type="text" value={readTime} readOnly />
          </label>
        </div>
        <div className="editor__image-bar">
          <div className="editor__image-group">
            <span className="editor__image-label">Image</span>
            {preview ? (
              <>
                <img src={preview} alt="preview" className="editor__meta-thumb" />
                <button type="button" className="editor__meta-delete" title="Delete image" onClick={async () => {
                  if (!window.confirm("Delete this image?")) return;
                  try {
                    const imgPath = storagePathFromURL(preview);
                    if (imgPath) await deleteObject(ref(storage, imgPath)).catch(() => {});
                    await updateDoc(doc(db, "content", project!, contentType!, docId!), { preview: "" });
                    setPreview("");
                    setCroppedPreview("");
                    toast.success("Image deleted");
                  } catch { toast.error("Delete failed"); }
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                </button>
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && uploadPreview(e.target.files[0])}
                  disabled={uploading}
                />
                {uploading && <span className="editor__meta-uploading">Uploading...</span>}
              </>
            )}
          </div>
          {preview && (
            <div className="editor__image-group">
              <span className="editor__image-label">Preview</span>
              <button type="button" className="editor__crop-btn" onClick={() => setShowCropper(true)}>
                Create Preview
              </button>
              {croppedPreview && (
                <>
                  <img src={croppedPreview} alt="cropped" className="editor__meta-thumb" />
                  <button type="button" className="editor__meta-delete" title="Delete preview" onClick={async () => {
                    if (!window.confirm("Delete cropped preview?")) return;
                    try {
                      const cropPath = storagePathFromURL(croppedPreview);
                      if (cropPath) await deleteObject(ref(storage, cropPath)).catch(() => {});
                      await updateDoc(doc(db, "content", project!, contentType!, docId!), { croppedPreview: "" });
                      setCroppedPreview("");
                      toast.success("Preview deleted");
                    } catch { toast.error("Delete failed"); }
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showCropper && preview && (
        <ImageCropper
          src={preview}
          onClose={() => setShowCropper(false)}
          onSave={async (blob, aspect) => {
            if (!project || !contentType || !docId) return;
            const path = `content/${project}/${contentType}/${docId}/_preview_${aspect.replace(":", "x")}.jpg`;
            const storageRef = ref(storage, path);
            await uploadBytes(storageRef, blob);
            const url = await getDownloadURL(storageRef);
            setCroppedPreview(url);
            await updateDoc(doc(db, "content", project, contentType, docId), { croppedPreview: url });
            toast.success(`Preview ${aspect} saved`);
            setShowCropper(false);
          }}
        />
      )}

      {genActive && (
        <div className="editor__gen-banner">
          <span className="editor__gen-banner-spinner" />
          <span>
            {genStatus === "queued"
              ? "Generation queued — article is being prepared"
              : "Generating content — editor is locked until it finishes"}
          </span>
        </div>
      )}
      {isStuck && (
        <div className="editor__gen-banner editor__gen-banner--warn">
          <span className="editor__gen-banner-msg">
            Generation seems stuck (started {Math.floor((now - (genStartedMs || now)) / 60_000)} min ago, no result). Pipeline may have died.
          </span>
          <button
            className="editor__gen-banner-retry"
            onClick={retryGeneration}
            disabled={retrying || !genMeta}
          >
            {retrying ? "Restarting…" : "↻ Restart"}
          </button>
          <button
            className="editor__gen-banner-clear"
            onClick={clearStuckGeneration}
          >
            Clear marker
          </button>
        </div>
      )}
      {genStatus === "failed" && (
        <div className="editor__gen-banner editor__gen-banner--error">
          <span className="editor__gen-banner-msg">
            Generation failed{genError ? `: ${genError}` : ""}
          </span>
          <button
            className="editor__gen-banner-retry"
            onClick={retryGeneration}
            disabled={retrying || !genMeta}
          >
            {retrying ? "Retrying…" : `↻ Retry ${genMeta?.kind === "rewrite" ? "rewrite" : "generation"}`}
          </button>
        </div>
      )}

      {mdLoading ? (
        <div className="editor__md-loading">Loading markdown…</div>
      ) : mode === "edit" ? (
        <textarea
          className="editor__markdown"
          value={markdown}
          onChange={(e) => setMarkdown(e.target.value)}
          placeholder="Write your content in Markdown..."
          spellCheck={false}
          readOnly={genActive}
        />
      ) : (
        <div
          className="editor__preview"
          dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(markdown) }}
        />
      )}

      {showRewrite && project && contentType && docId && (
        <RewriteModal
          project={project}
          contentType={contentType}
          docId={docId}
          locale={LANG}
          userEmail={user?.email || firebaseUser?.email || "unknown"}
          onClose={() => setShowRewrite(false)}
          onQueued={() => {
            setShowRewrite(false);
            navigate(`/cms/${project}/${contentType}`);
          }}
        />
      )}
    </div>
  );
};

interface RewriteModalProps {
  project: string;
  contentType: string;
  docId: string;
  locale: string;
  userEmail: string;
  onClose: () => void;
  onQueued: () => void;
}

type Length = "short" | "medium" | "long";

const LENGTH_OPTIONS: Array<{ id: Length; label: string; hint: string }> = [
  { id: "short", label: "Short", hint: "~300–500 words" },
  { id: "medium", label: "Medium", hint: "~600–900 words" },
  { id: "long", label: "Long", hint: "~1200–1800 words" },
];

const RewriteModal: React.FC<RewriteModalProps> = ({ project, contentType, docId, locale, userEmail, onClose, onQueued }) => {
  const [direction, setDirection] = useState("");
  const [tone, setTone] = useState("");
  const [length, setLength] = useState<Length>("medium");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasRules, setHasRules] = useState(false);

  useEffect(() => {
    getDoc(doc(db, "content", project, "_config", "ai_generation"))
      .then((snap) => {
        if (snap.exists()) {
          const cfg = (snap.data() as any)[contentType] || {};
          if (cfg.defaultLength) setLength(cfg.defaultLength);
          if (cfg.defaultTone) setTone(cfg.defaultTone);
          setHasRules(!!cfg.rules);
        }
      })
      .catch(() => {});
  }, [project, contentType]);

  const submit = async () => {
    const d = direction.trim();
    if (!d) {
      toast.error("Direction is required — describe what to change");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(REWRITE_FN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project,
          contentType,
          docId,
          locale,
          direction: d,
          tone,
          length,
          notes,
          user: userEmail,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      toast.success("Rewrite queued — back to list");
      onQueued();
    } catch (e: any) {
      toast.error(`Rewrite failed: ${e.message}`);
    }
    setSubmitting(false);
  };

  return (
    <div className="cms-modal__overlay" onClick={onClose}>
      <div className="cms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cms-modal__header">
          <h3>✨ Rewrite article</h3>
          <button className="cms-modal__close" onClick={onClose}>×</button>
        </div>

        <div className="cms-modal__body">
          {!hasRules && (
            <div className="cms-modal__warn">
              No project-level rules set. Configure them in <strong>AI Generation</strong> tab for consistent results.
            </div>
          )}

          <label className="cms-modal__field">
            <span>Direction * <em>(what to change)</em></span>
            <textarea
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              placeholder="e.g. Make it shorter and punchier. Add the biological mechanism behind enamel thinning. Replace vague claims with specific compound names."
              rows={4}
              autoFocus
            />
          </label>

          <div className="cms-modal__row">
            <label className="cms-modal__field">
              <span>Tone</span>
              <input
                type="text"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                placeholder="clinical, directive, anti-marketing"
              />
            </label>
          </div>

          <div className="cms-modal__field">
            <span>Length</span>
            <div className="cms-modal__radio-group">
              {LENGTH_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  className={`cms-modal__radio ${length === opt.id ? "cms-modal__radio--active" : ""}`}
                >
                  <input
                    type="radio"
                    name="rewrite-length"
                    checked={length === opt.id}
                    onChange={() => setLength(opt.id)}
                  />
                  <span className="cms-modal__radio-label">{opt.label}</span>
                  <span className="cms-modal__radio-hint">{opt.hint}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="cms-modal__field">
            <span>Extra notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything else the rewriter should know"
              rows={2}
            />
          </label>
        </div>

        <div className="cms-modal__footer">
          <button className="editor__save-btn editor__save-btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="editor__save-btn" onClick={submit} disabled={submitting}>
            {submitting ? "Queuing..." : "Rewrite"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Editor;
